import DOMPurify from "dompurify";
import { JSX, useCallback, useEffect, useMemo, useRef } from "react";

export type FormatType = 'bold' | 'italic' | 'underline' | 'align_left' | 'align_center' | 'align_right' | 'justifyLeft' | 'justifyCenter' | 'justifyRight' | 'insertUnorderedList' | 'insertOrderedList' | 'foreColor';
export const ALLOWED_TAGS = ['p', 'a', 'b', 'i', 'u', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'img', 'span', 'div', 'table', 'tr', 'th', 'td', 'br', 'ol', 'ul', 'li', 'html', 'head', 'style', 'body', 'title'];

export interface RichTextFieldMethods {
  applyFormat: (format: FormatType, value?: string) => void;
  addLink: (text: string, url: string) => void;
  addImage: (imageUrl: string) => void;
  restoreLastSelection: () => void;
  insertCustom: (newElement: HTMLElement) => void;
  resetEditorContent: (value: string) => Promise<void>;
}

export interface RichTextField extends RichTextFieldMethods {
  RichTextfield: JSX.Element;
}

type Props = {
  editorRef: React.MutableRefObject<HTMLDivElement | null>;
  content: string;
  setContent: React.Dispatch<React.SetStateAction<string>> | ((val: string) => void);
  onFocus?: () => void;
  disabled?: boolean;
}

export default function useRichTextfield(props: Props): RichTextField {
  const { editorRef, content, setContent, onFocus, disabled } = props;

  const lastSelectionRef = useRef<Range | null>(null);

  const sanitizeHTML = useCallback((dangerousString: string) => {
    return DOMPurify.sanitize(dangerousString, { ALLOWED_TAGS, ALLOWED_ATTR: ['style', 'src', 'alt', 'href', 'target', 'rel', 'width', 'height', 'class'], WHOLE_DOCUMENT: true });
  }, []);

  const handlePasteAttempt = () => {
    navigator.clipboard.readText().then(clipText => {
      document.execCommand('insertText', false, clipText);
    });
  }

  const escapeListAtLi = (li: HTMLLIElement, list: Element, selection: Selection): boolean => {
    const isFirstLi = list.firstElementChild === li;
    const isLastLi = list.lastElementChild === li;

    const div = document.createElement('div');
    while (li.firstChild) div.appendChild(li.firstChild);
    if (div.childNodes.length === 0) div.appendChild(document.createElement('br'));

    if (isFirstLi && isLastLi) {
      list.parentElement?.insertBefore(div, list);
      list.remove();
    } else if (isFirstLi) {
      list.parentElement?.insertBefore(div, list);
      li.remove();
    } else if (isLastLi) {
      list.parentElement?.insertBefore(div, list.nextSibling);
      li.remove();
    } else {
      const tail = document.createElement(list.tagName.toLowerCase());
      const sourceStyle = (list as HTMLElement).style.cssText;
      if (sourceStyle) tail.style.cssText = sourceStyle;
      let next = li.nextElementSibling;
      while (next) {
        const followingNext = next.nextElementSibling;
        tail.appendChild(next);
        next = followingNext;
      }
      list.parentElement?.insertBefore(div, list.nextSibling);
      list.parentElement?.insertBefore(tail, div.nextSibling);
      li.remove();
    }

    const newRange = document.createRange();
    newRange.setStart(div, 0);
    newRange.collapse(true);
    selection.removeAllRanges();
    selection.addRange(newRange);

    return true;
  };

  const isLiVisuallyEmpty = (li: HTMLLIElement): boolean => {
    const text = (li.textContent || '').replace(/\u200B/g, '').trim();
    if (text !== '') return false;
    return !li.querySelector('img, iframe, video, audio, table, hr');
  };

  const handleListEnter = (e: React.KeyboardEvent<HTMLDivElement>, selection: Selection, range: Range): boolean => {
    if (!selection.isCollapsed) return false;
    if (!editorRef.current) return false;

    const startEl: Element | null = range.startContainer.nodeType === Node.TEXT_NODE
      ? range.startContainer.parentElement
      : (range.startContainer as Element);
    const li = startEl?.closest?.('li') as HTMLLIElement | null;
    if (!li || !editorRef.current.contains(li)) return false;

    const list = li.parentElement;
    if (!list || (list.tagName !== 'UL' && list.tagName !== 'OL')) return false;

    if (!isLiVisuallyEmpty(li)) return false;

    e.preventDefault();
    return escapeListAtLi(li, list, selection);
  };

  const handleListBackspace = (e: React.KeyboardEvent<HTMLDivElement>, selection: Selection, range: Range): boolean => {
    if (!selection.isCollapsed) return false;
    if (!editorRef.current) return false;

    const startEl: Element | null = range.startContainer.nodeType === Node.TEXT_NODE
      ? range.startContainer.parentElement
      : (range.startContainer as Element);
    const li = startEl?.closest?.('li') as HTMLLIElement | null;
    if (!li || !editorRef.current.contains(li)) return false;

    const list = li.parentElement;
    if (!list || (list.tagName !== 'UL' && list.tagName !== 'OL')) return false;

    const caretRange = document.createRange();
    caretRange.selectNodeContents(li);
    caretRange.setEnd(range.startContainer, range.startOffset);
    const isAtStart = caretRange.toString().length === 0;
    if (!isAtStart) return false;

    e.preventDefault();

    const isFirstLi = list.firstElementChild === li;
    const prevLi = li.previousElementSibling as HTMLLIElement | null;

    if (isFirstLi || !prevLi) {
      const div = document.createElement('div');
      while (li.firstChild) div.appendChild(li.firstChild);
      if (div.childNodes.length === 0) div.appendChild(document.createElement('br'));
      list.parentElement?.insertBefore(div, list);
      li.remove();
      if (list.children.length === 0) list.remove();

      const newRange = document.createRange();
      newRange.setStart(div, 0);
      newRange.collapse(true);
      selection.removeAllRanges();
      selection.addRange(newRange);
    } else {
      const mergeOffset = prevLi.childNodes.length;
      while (li.firstChild) prevLi.appendChild(li.firstChild);
      li.remove();

      const newRange = document.createRange();
      newRange.setStart(prevLi, mergeOffset);
      newRange.collapse(true);
      selection.removeAllRanges();
      selection.addRange(newRange);
    }

    return true;
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {

    if (e.key === 'v' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      handlePasteAttempt();
    }

    if (['Enter', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Backspace'].includes(e.key) && (window.getSelection()?.rangeCount || -1) > 0) {
      const selection = window.getSelection()!;
      const range = selection.getRangeAt(0);
      const parentAnchor = range.startContainer.parentElement?.closest('a');

      if (!parentAnchor && (e.key === 'Backspace' || e.key === 'Enter')) {
        const handled = e.key === 'Backspace'
          ? handleListBackspace(e, selection, range)
          : handleListEnter(e, selection, range);
        if (handled) {
          if (editorRef.current) {
            setContent(sanitizeHTML(editorRef.current.innerHTML));
            saveLastSelection();
          }
          return;
        }
      }

      if (parentAnchor) {
        switch (e.key) {
          case 'Enter':
            e.preventDefault();
            const newLine = document.createElement('br');
            parentAnchor.insertAdjacentElement('afterend', newLine);
            const newRange = document.createRange();
            newRange.setStartAfter(newLine);
            newRange.collapse(true);
            selection.removeAllRanges();
            selection.addRange(newRange);
            break;
          case 'ArrowUp':
          case 'ArrowLeft':
            if (range.startOffset === 0 && !parentAnchor.parentElement?.previousSibling) {
              parentAnchor.insertAdjacentText('beforebegin', '\u200B');
              const newRange = document.createRange();
              newRange.setStartBefore(parentAnchor.previousSibling!);
              newRange.collapse(true);
              selection.removeAllRanges();
              selection.addRange(newRange);
            }
            break;
          case 'ArrowDown':
          case 'ArrowRight':
            if (range.endOffset === parentAnchor.textContent?.length && !parentAnchor.parentElement?.nextSibling) {
              parentAnchor.insertAdjacentText('afterend', '\u200B');
              const newRange = document.createRange();
              newRange.setStartAfter(parentAnchor.nextSibling!);
              newRange.collapse(true);
              selection.removeAllRanges();
              selection.addRange(newRange);
            }
            break;
          case 'Backspace':
            if (selection.toString() === parentAnchor.textContent) {
              e.preventDefault();
              const br = document.createElement('br');
              parentAnchor.innerText = '';
              parentAnchor.insertAdjacentElement('afterbegin', br);
            }
            break;
          default:
            break;
        }
      }
    }

    if (editorRef.current) {
      setContent(sanitizeHTML(editorRef.current.innerHTML));
      saveLastSelection();
    }
  };


  const handleBlur = () => {
    clearImageSelection();

    if (editorRef.current) {
      setContent(sanitizeHTML(editorRef.current.innerHTML));
    }
  };

  const resetEditorContent = async (value: string) => {
    await new Promise(resolve => setTimeout(resolve, 50));
    if (editorRef.current)
      editorRef.current.innerHTML = value;
  }

  const saveLastSelection = () => {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return;
    const range = selection.getRangeAt(0);
    if (editorRef.current && editorRef.current.contains(range.commonAncestorContainer))
      lastSelectionRef.current = range;
  }

  const restoreLastSelection = () => {
    const selection = window.getSelection();
    const savedSelection = lastSelectionRef.current;
    if (!selection || !savedSelection) return;
    selection.removeAllRanges();
    selection.addRange(savedSelection);
  }

  const ensureSelectionInEditor = (): Selection | null => {
    if (!editorRef.current) return null;

    const selection = window.getSelection();
    if (selection && selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);
      if (editorRef.current.contains(range.commonAncestorContainer))
        return selection;
    }

    if (lastSelectionRef.current && editorRef.current.contains(lastSelectionRef.current.commonAncestorContainer)) {
      const sel = window.getSelection();
      if (sel) {
        sel.removeAllRanges();
        sel.addRange(lastSelectionRef.current);
        return sel;
      }
    }

    editorRef.current.focus();
    const endRange = document.createRange();
    endRange.selectNodeContents(editorRef.current);
    endRange.collapse(false);
    const sel = window.getSelection();
    if (sel) {
      sel.removeAllRanges();
      sel.addRange(endRange);
    }
    return sel;
  };

  const styleListEl = (list: HTMLElement, format: FormatType) => {
    list.style.listStyle = format === 'insertOrderedList' ? 'auto' : 'disc';
    list.style.paddingLeft = '40px';
  };

  const unwrapList = (list: HTMLElement) => {
    const parent = list.parentNode;
    if (!parent) return;
    const items = Array.from(list.children).filter(c => c.tagName.toLowerCase() === 'li');
    for (const li of items) {
      const wrapper = document.createElement('div');
      while (li.firstChild) wrapper.appendChild(li.firstChild);
      parent.insertBefore(wrapper, list);
    }
    parent.removeChild(list);
  };

  const insertList = (format: FormatType) => {
    if (!editorRef.current) return;

    const selection = ensureSelectionInEditor();
    if (!selection || selection.rangeCount === 0) return;

    const range = selection.getRangeAt(0);
    if (!editorRef.current.contains(range.commonAncestorContainer)) return;

    const targetTag = format === 'insertOrderedList' ? 'ol' : 'ul';

    const startEl: Element | null = range.startContainer.nodeType === Node.TEXT_NODE
      ? range.startContainer.parentElement
      : (range.startContainer as Element);
    const closestList = startEl?.closest?.('ul, ol') ?? null;

    if (closestList && editorRef.current.contains(closestList)) {
      const currentTag = closestList.tagName.toLowerCase();
      if (currentTag === targetTag) {
        unwrapList(closestList as HTMLElement);
      } else {
        const newList = document.createElement(targetTag);
        styleListEl(newList, format);
        while (closestList.firstChild) newList.appendChild(closestList.firstChild);
        closestList.replaceWith(newList);
      }
      saveLastSelection();
      setContent(sanitizeHTML(editorRef.current.innerHTML));
      return;
    }

    let block: Node = range.startContainer;
    while (block && block.nodeType === Node.TEXT_NODE) {
      block = block.parentNode!;
    }
    if (!editorRef.current.contains(block)) return;
    if (block === editorRef.current) {
      const div = document.createElement('div');
      while (editorRef.current.firstChild) div.appendChild(editorRef.current.firstChild);
      editorRef.current.appendChild(div);
      block = div;
    }

    const lineRange = document.createRange();
    lineRange.selectNodeContents(block);

    const newLine = document.createElement('li');
    newLine.appendChild(lineRange.extractContents());
    const list = document.createElement(targetTag);
    styleListEl(list, format);
    list.appendChild(newLine);
    lineRange.insertNode(list);

    saveLastSelection();
    setContent(sanitizeHTML(editorRef.current.innerHTML));
  }

  const applyFormat = (format: FormatType, value?: string) => {
    if (!editorRef.current) return;

    const selection = ensureSelectionInEditor();
    if (!selection || selection.rangeCount === 0) return;

    if (['insertUnorderedList', 'insertOrderedList'].includes(format))
      return insertList(format);

    document.execCommand(format, false, value || '');
    saveLastSelection();
    setContent(sanitizeHTML(editorRef.current.innerHTML));
  }

  const addLink = (text: string, url: string) => {
    if (!editorRef.current) return;

    const range = lastSelectionRef.current;

    if (range) {
      range.deleteContents();
      range.extractContents();

      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.target = '_blank';
      anchor.rel = 'noopener noreferrer';
      anchor.text = text;
      anchor.style.textDecoration = 'underline';
      anchor.style.color = 'blue';

      range.insertNode(anchor);
      saveLastSelection();
      setContent(sanitizeHTML(editorRef.current.innerHTML));
    }
  }

  const addImage = (imageUrl: string) => {
    if (!editorRef.current) return;

    const range = lastSelectionRef.current;
    const img = document.createElement('img');
    img.src = imageUrl;
    img.alt = 'User provided content';
    img.width = 400;
    if (range) {
      img.appendChild(range.extractContents());
      range.insertNode(img);
      const newRange = document.createRange();
      newRange.setStartAfter(img);
      newRange.collapse(true);
    } else {
      editorRef.current.appendChild(img);
    }
    saveLastSelection();
    setContent(sanitizeHTML(editorRef.current.innerHTML));
  };

  const insertCustom = (newElement: HTMLElement) => {
    if (!(ALLOWED_TAGS.includes(newElement.tagName.toLowerCase())))
      return;

    if (!editorRef.current) return;

    const range = lastSelectionRef.current;
    if (range) {
      newElement.textContent += range.toString();
      range.extractContents()
      range.insertNode(newElement);
      const newRange = document.createRange();
      newRange.setStartAfter(newElement);
      newRange.collapse(true);
    }

    saveLastSelection();
    setContent(sanitizeHTML(editorRef.current.innerHTML));
  }

  const activeResizeRef = useRef<{ img: HTMLImageElement; startX: number; startWidth: number } | null>(null);

  const clearImageSelection = () => {
    if (!editorRef.current) return;
    // Unwrap images from resize wrappers first
    editorRef.current.querySelectorAll('span.img-resize-wrapper').forEach(wrapper => {
      const img = wrapper.querySelector('img');
      if (img) {
        img.style.outline = '';
        img.style.cursor = '';
        wrapper.parentNode?.insertBefore(img, wrapper);
      }
      wrapper.remove();
    });
    // Clean up any stray handles
    editorRef.current.querySelectorAll('.img-resize-handle').forEach(el => el.remove());
  };

  const selectImage = (img: HTMLImageElement) => {
    if (disabled) return;
    clearImageSelection();
    img.style.outline = '2px solid #3b82f6';
    img.style.cursor = 'default';

    const handle = document.createElement('div');
    handle.className = 'img-resize-handle';
    handle.contentEditable = 'false';
    handle.style.cssText = 'width:10px;height:10px;background:#3b82f6;position:absolute;top:-5px;right:-5px;cursor:nesw-resize;border-radius:2px;z-index:10;';

    const wrapper = document.createElement('span');
    wrapper.contentEditable = 'false';
    wrapper.style.cssText = 'position:relative;display:inline-block;';
    wrapper.className = 'img-resize-wrapper';

    img.parentNode?.insertBefore(wrapper, img);
    wrapper.appendChild(img);
    wrapper.appendChild(handle);

    handle.addEventListener('mousedown', (e) => {
      e.preventDefault();
      e.stopPropagation();
      activeResizeRef.current = { img, startX: e.clientX, startWidth: img.offsetWidth };

      const onMouseMove = (ev: MouseEvent) => {
        if (!activeResizeRef.current) return;
        const newWidth = Math.max(50, activeResizeRef.current.startWidth + (ev.clientX - activeResizeRef.current.startX));
        activeResizeRef.current.img.style.width = newWidth + 'px';
        activeResizeRef.current.img.removeAttribute('width');
      };

      const onMouseUp = () => {
        activeResizeRef.current = null;
        document.removeEventListener('mousemove', onMouseMove);
        document.removeEventListener('mouseup', onMouseUp);
        clearImageSelection();
        if (editorRef.current) {
          setContent(sanitizeHTML(editorRef.current.innerHTML));
        }
      };

      document.addEventListener('mousemove', onMouseMove);
      document.addEventListener('mouseup', onMouseUp);
    });
  };

  const handleEditorClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement;
    if (target.tagName === 'IMG') {
      e.preventDefault();
      selectImage(target as HTMLImageElement);
    } else if (!target.closest('.img-resize-handle')) {
      clearImageSelection();
    }
    saveLastSelection();
  };

  const RichTextfield = useMemo(() => (
    <div
      className="h-full min-h-[150px] outline-none border-none overflow-auto flex-grow font-eina"
      ref={editorRef}
      contentEditable={!disabled}
      onKeyDown={handleKeyDown}
      onKeyUp={saveLastSelection}
      onMouseUp={handleEditorClick}
      onMouseDown={saveLastSelection}
      dangerouslySetInnerHTML={{ __html: content }}
      suppressContentEditableWarning={true}
      onBlur={handleBlur}
      onFocus={onFocus}
    />
  ) // eslint-disable-next-line react-hooks/exhaustive-deps
    , [editorRef, disabled]);

  useEffect(() => {
    if (editorRef.current?.innerHTML?.length === 0) {
      editorRef.current.innerHTML = sanitizeHTML(content);
    }
  }, [content, editorRef, sanitizeHTML]);

  return {
    RichTextfield,
    applyFormat,
    addLink,
    addImage,
    restoreLastSelection,
    insertCustom,
    resetEditorContent,
  }
}
